import { useEffect, useRef, useState } from 'react';

import { api, queryString } from './api';
import { CloseIcon, SearchIcon } from './icons';
import { visibleProductDetailName } from './product-display';

interface ProductSold {
  productId: number;
  productDetailId: number;
  productName?: string;
  detailName?: string;
  upc?: string;
  sku: string;
  unitName?: string;
  totalQuantity: number;
  totalAmount: number;
  giftQuantity: number;
}

interface ReportResponse {
  items: ProductSold[];
  totalCount: number;
  nextOffset: number | null;
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

export function OutboundProductsModal({ from, to, onClose }: {
  from: string;
  to: string;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ProductSold[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const sequence = useRef(0);

  useEffect(() => {
    const current = ++sequence.current;
    const timer = window.setTimeout(async () => {
      setRows([]);
      setTotalCount(0);
      setLoading(true);
      setError('');
      try {
        let offset: number | null = 0;
        const collected: ProductSold[] = [];
        const seen = new Set<string>();
        do {
          const result: ReportResponse = await api<ReportResponse>(`/api/reports/outbound-products?${queryString({ from, to, q: q || undefined, offset, limit: 500 })}`);
          if (current !== sequence.current) return;
          for (const item of result.items) {
            const key = `${item.productId}:${item.productDetailId}:${item.sku}`;
            if (seen.has(key)) throw new Error('Báo cáo có dữ liệu lặp, đã dừng tải.');
            seen.add(key);
            collected.push(item);
          }
          setRows([...collected]);
          setTotalCount(result.totalCount);
          offset = result.nextOffset;
          if (offset !== null) await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
        } while (offset !== null);
      } catch (reason) {
        if (current === sequence.current) setError(reason instanceof Error ? reason.message : 'Không thể tải báo cáo.');
      } finally {
        if (current === sequence.current) setLoading(false);
      }
    }, q ? 250 : 0);
    return () => {
      window.clearTimeout(timer);
      sequence.current += 1;
    };
  }, [from, q, to]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose]);

  return (
    <div className="drawer-backdrop" role="presentation">
      <section className="products-report-modal" role="dialog" aria-modal="true" aria-labelledby="products-report-title">
        <header className="drawer-header"><h2 id="products-report-title">Sản phẩm đã bán · {from} — {to}</h2><button type="button" className="icon-button" onClick={onClose} aria-label="Đóng"><CloseIcon /></button></header>
        <div className="report-toolbar"><div className="search-box"><SearchIcon /><input value={q} onChange={(event) => setQ(event.target.value)} placeholder="Tên, mã sản phẩm hoặc mã lô" aria-label="Tìm sản phẩm đã bán" />{q ? <button type="button" onClick={() => setQ('')}>Xóa</button> : null}</div><span>{loading ? `Đang tải ${money(rows.length)} / ${money(totalCount)} dòng` : `${money(rows.length)} dòng`}</span></div>
        <div className="products-report-table-wrap">
          <table className="products-report-table"><thead><tr><th>Sản phẩm</th><th>Phân loại</th><th>Lô</th><th className="align-right">Số lượng</th><th>ĐVT</th><th className="align-right">Quà tặng</th><th className="align-right">Doanh thu</th></tr></thead><tbody>
            {!loading && rows.length === 0 ? <tr><td colSpan={7}><div className="empty-list"><SearchIcon /><strong>Không có sản phẩm</strong></div></td></tr> : null}
            {rows.map((row) => <tr key={`${row.productId}:${row.productDetailId}:${row.sku}`}><td><strong>{row.productName || 'Sản phẩm không tồn tại'}</strong><span>{row.upc || '—'}</span></td><td>{visibleProductDetailName(row.productName, row.detailName)}</td><td><span className="return-lot-badge">{row.sku}</span></td><td className="align-right"><b>{money(row.totalQuantity)}</b></td><td>{row.unitName || '—'}</td><td className="align-right">{money(row.giftQuantity)}</td><td className="align-right"><b>{money(row.totalAmount)} ₫</b></td></tr>)}
          </tbody></table>
        </div>
        {error ? <div className="return-error" role="alert">{error}</div> : null}
        <footer className={`data-status ${error ? 'error' : loading ? 'loading' : 'ready'}`}><span className="data-progress" style={{ width: totalCount ? `${Math.min(100, rows.length / totalCount * 100)}%` : loading ? '0%' : '100%' }} /><span className="status-indicator">{loading ? <span className="mini-spinner" /> : null}</span><span>{loading ? 'Đang tải đầy đủ dữ liệu báo cáo…' : `Đã tải đủ ${money(rows.length)} dòng`}</span></footer>
      </section>
    </div>
  );
}
